import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  type SkillsAssessment,
  type SkillsAssessmentStatus,
  SkillsAssessmentWorkerStatus,
} from "@src/appV2/SkillsAssessment/types";
import { checkIsWorkerAllowedToScheduleAssessment } from "@src/appV2/SkillsAssessment/utils/checkIsWorkerAllowedToScheduleAssessment";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { isWorkerTimezoneMatchingDevice } from "../../utils";
import { ScheduleOrientation } from "../Schedule/ScheduleOrientation";
import { ClipboardOrientationFailed } from "./ClipboardOrientationFailed";
import { ClipboardOrientationPassed } from "./ClipboardOrientationPassed";
import { ClipboardOrientationScheduled } from "./ClipboardOrientationScheduled";

interface Props {
  isRefetchingData: boolean;
  assessment: SkillsAssessment;
  assessmentStatus?: SkillsAssessmentStatus;
  scheduledFor?: string;
  onSchedule: () => void;
  onReschedule: () => void;
  onComplete: () => void;
  onBackAfterFail: () => void;
  qualificationName: string;
}

export function ClipboardOrientationStatus(props: Props) {
  const {
    isRefetchingData,
    assessment,
    assessmentStatus,
    scheduledFor,
    onSchedule,
    qualificationName,
    onReschedule,
    onComplete,
    onBackAfterFail,
  } = props;

  const worker = useDefinedWorker();

  const { retakeAllowedAfter } = assessmentStatus?.attributes ?? {};

  const isSchedulingAllowed = checkIsWorkerAllowedToScheduleAssessment({
    retakeAllowedAfter: assessmentStatus?.attributes.retakeAllowedAfter,
    assessmentType: assessmentStatus?.attributes.type,
    assessmentStatus: assessmentStatus?.attributes.status,
    assessmentScheduledFor: scheduledFor,
  });

  const showTimezone = !isWorkerTimezoneMatchingDevice(worker.tmz);

  return (
    <Stack height="100%" width="100%">
      {isSchedulingAllowed ? (
        <Stack pt={6} flex={1}>
          <ScheduleOrientation
            isRefetchingData={isRefetchingData}
            assessment={assessment}
            qualificationName={qualificationName}
            onSuccess={onSchedule}
          />
        </Stack>
      ) : (
        <>
          {assessmentStatus?.attributes.status === SkillsAssessmentWorkerStatus.PENDING &&
            isDefined(scheduledFor) && (
              <Stack pt={6} flex={1}>
                <ClipboardOrientationScheduled
                  showTimezone={showTimezone}
                  assessment={assessment}
                  scheduledFor={scheduledFor}
                  assessmentStatus={assessmentStatus}
                  isRefetchingData={isRefetchingData}
                  onReschedule={onReschedule}
                />
              </Stack>
            )}
          {assessmentStatus?.attributes.status === SkillsAssessmentWorkerStatus.FAILED && (
            <ClipboardOrientationFailed
              scheduledFor={scheduledFor}
              retakeAllowedAfter={retakeAllowedAfter}
              onBack={onBackAfterFail}
            />
          )}

          {assessmentStatus?.attributes.status === SkillsAssessmentWorkerStatus.PASSED && (
            <ClipboardOrientationPassed onComplete={onComplete} />
          )}
        </>
      )}
    </Stack>
  );
}
